// One phase. The five rules every trader reads first, each with its number and
// its own plain sentence; everything else is one disclosure below.

import { createSignal, Show } from 'solid-js';

import type { RuleBuilderState } from '../core/ruleRegistry';
import type { Authority, StepBuilderInput } from '../form';
import Dropdown from './Dropdown';
import HeadlineBlock, { MONEY } from './HeadlineBlock';
import PhaseOptions from './PhaseOptions';
import RuleParams from './ruleParams/RuleParams';
import StepRulesEditor from './StepRulesEditor';
import { useTemplatePayoutWords } from './builderWordsContext';

interface PhaseEditorProps {
  authority: Authority;
  step: StepBuilderInput;
  /** The drawer's own sentence for a step term ("profit-target") or a rule id. */
  sentence: (key: string) => string;
  /** The profit target's unit. It is held above this component so switching
   *  phases does not forget which unit an empty field is waiting for. */
  targetUnit: string;
  onTargetUnit: (unit: string) => void;
  isFunded: boolean;
  onUpdateStep: (partial: Partial<StepBuilderInput>) => void;
  onUpdateRules: (rules: RuleBuilderState[]) => void;
  advancedOpen: Record<string, boolean>;
  onToggleAdvanced: (key: 'risk_limits' | 'trading_restrictions') => void;
}

const MONEY_UNITS = ['%', MONEY];
const DRAWDOWN_FAMILIES = ['max_total_drawdown', 'max_trailing_drawdown'];

export default function PhaseEditor(props: PhaseEditorProps) {
  const payout = useTemplatePayoutWords();
  const [query, setQuery] = createSignal('');

  const rowOf = (family: string) => props.step.rules.find((rule) => rule.ruleType === family);
  const liveRow = (family: string) => {
    const row = rowOf(family);
    return row?.enabled ? row : undefined;
  };
  /**
   * Whether the floor trails.
   *
   * A limit with no number yet is not enabled, so the enabled rows cannot
   * answer for an empty field. The trailing row's `update_mode` is the author's
   * own answer — picking a fixed floor clears it — which is why a mode chosen
   * before a number is typed survives until the number arrives.
   */
  const trailing = () => {
    if (liveRow('max_trailing_drawdown')) return true;
    if (liveRow('max_total_drawdown')) return false;
    return rowOf('max_trailing_drawdown')?.params['update_mode'] !== undefined;
  };
  /** The drawdown on screen: the one the chosen mode names. */
  const drawdown = () => rowOf(trailing() ? 'max_trailing_drawdown' : 'max_total_drawdown');
  const unitOf = (row: RuleBuilderState | undefined) => (row?.valueMode === 'absolute' ? MONEY : '%');
  const isHeadline = (rule: RuleBuilderState) =>
    rule.ruleType === 'max_daily_loss' || DRAWDOWN_FAMILIES.includes(rule.ruleType);
  const otherRules = () => props.step.rules.filter((rule) => rule.enabled && !isHeadline(rule)).length;

  function patch(match: (rule: RuleBuilderState) => boolean, partial: Partial<RuleBuilderState>) {
    props.onUpdateRules(props.step.rules.map((rule) => (match(rule) ? { ...rule, ...partial } : rule)));
  }

  /** A limit with no number is a limit nobody sold, so the row goes quiet. */
  const setLimit = (family: string, value: string) =>
    patch((rule) => rule.ruleType === family, { value, enabled: value.trim() !== '' });

  /** Switching unit clears the number: 5% and an amount of 5 are different limits. */
  const setLimitUnit = (families: string[], unit: string) =>
    patch((rule) => families.includes(rule.ruleType), {
      valueMode: unit === MONEY ? 'absolute' : 'percent',
      value: '',
      enabled: false,
    });

  function setDrawdownValue(value: string) {
    const family = trailing() ? 'max_trailing_drawdown' : 'max_total_drawdown';
    props.onUpdateRules(props.step.rules.map((rule) => {
      if (rule.ruleType === family) return { ...rule, value, enabled: value.trim() !== '' };
      if (DRAWDOWN_FAMILIES.includes(rule.ruleType)) return { ...rule, enabled: false };
      return rule;
    }));
  }

  /** Fixed or trailing is one choice, never two rules at once. */
  function setDrawdownMode(mode: string) {
    const value = drawdown()?.value ?? '';
    const fixed = mode === 'fixed';
    props.onUpdateRules(props.step.rules.map((rule) => {
      if (rule.ruleType === 'max_total_drawdown') {
        return { ...rule, value, enabled: fixed && value.trim() !== '' };
      }
      if (rule.ruleType === 'max_trailing_drawdown') {
        // A fixed floor has no update mode, and leaving one behind would make
        // the row claim a choice the author has just taken back.
        const { update_mode: _dropped, ...rest } = rule.params;
        return {
          ...rule,
          value,
          enabled: !fixed && value.trim() !== '',
          params: fixed ? rest : { ...rest, update_mode: mode === 'trailing_eod' ? 'eod' : 'realtime' },
        };
      }
      return rule;
    }));
  }

  const setParams = (id: string, params: Record<string, unknown>) =>
    patch((rule) => rule.id === id, { params });

  return (
    <div class="ch-blocks">
      <HeadlineBlock
        title={props.isFunded ? `${payout.payout_label} target` : 'Profit target'}
        sentence={props.sentence('profit-target') || 'No profit target: the phase never passes on profit alone.'}
        value={props.targetUnit === MONEY ? props.step.profitTargetAbs : props.step.profitTargetPct}
        unit={props.targetUnit}
        units={{ options: MONEY_UNITS, onPick: (unit) => {
          props.onUpdateStep({ profitTargetPct: '', profitTargetAbs: '' });
          props.onTargetUnit(unit);
        } }}
        step={0.5}
        onInput={(value) => props.onUpdateStep(
          props.targetUnit === MONEY ? { profitTargetAbs: value } : { profitTargetPct: value },
        )}
      />

      <HeadlineBlock
        title="Max daily loss"
        sentence={props.sentence(rowOf('max_daily_loss')?.id ?? '') || 'No daily loss limit.'}
        value={rowOf('max_daily_loss')?.value ?? ''}
        unit={unitOf(rowOf('max_daily_loss'))}
        units={{ options: MONEY_UNITS, onPick: (unit) => setLimitUnit(['max_daily_loss'], unit) }}
        step={0.5}
        onInput={(value) => setLimit('max_daily_loss', value)}
      >
        <Show when={rowOf('max_daily_loss')}>
          {(row) => <RuleParams rule={row()} onUpdateParams={(params) => setParams(row().id, params)} />}
        </Show>
      </HeadlineBlock>

      <HeadlineBlock
        title="Max total loss"
        sentence={props.sentence(drawdown()?.id ?? '') || 'No total loss limit.'}
        value={drawdown()?.value ?? ''}
        unit={unitOf(drawdown())}
        units={{ options: MONEY_UNITS, onPick: (unit) => setLimitUnit(DRAWDOWN_FAMILIES, unit) }}
        step={0.5}
        onInput={setDrawdownValue}
      >
        <div class="ch-field ch-field-sm">
          <span class="ch-label">The floor is</span>
          <Dropdown
            value={trailing()
              ? (drawdown()?.params['update_mode'] === 'eod' ? 'trailing_eod' : 'trailing_realtime')
              : 'fixed'}
            options={[
              { value: 'fixed', label: 'Fixed, from the starting balance' },
              { value: 'trailing_realtime', label: 'Trailing, moving up live' },
              { value: 'trailing_eod', label: 'Trailing, moving up at the end of each day' },
            ]}
            onChange={setDrawdownMode}
          />
        </div>
        <Show when={drawdown()}>
          {(row) => <RuleParams rule={row()} onUpdateParams={(params) => setParams(row().id, params)} />}
        </Show>
      </HeadlineBlock>

      <HeadlineBlock
        title="Minimum trading days"
        sentence={props.sentence('min-trading-days') || 'No minimum: the phase can pass on one day.'}
        value={props.step.minTradingDays}
        unit="days"
        onInput={(value) => props.onUpdateStep({ minTradingDays: value })}
      />

      <HeadlineBlock
        title="Time limit"
        sentence={props.sentence('time-limit') || 'No time limit.'}
        value={props.step.timeLimitDays}
        unit="days"
        onInput={(value) => props.onUpdateStep({ timeLimitDays: value })}
      />

      <PhaseOptions step={props.step} onUpdate={props.onUpdateStep} />

      <details class="ch-card ch-disclose">
        <summary class="ch-disclose-head">
          More rules
          <span class="ch-meta">{otherRules() === 0 ? 'none added' : `${otherRules()} added`}</span>
        </summary>
        <div class="ch-disclose-body">
          <input
            class="ch-search"
            type="search"
            value={query()}
            placeholder="Find a rule… news, weekend, stop loss"
            aria-label="Find a rule"
            onInput={(event) => setQuery(event.currentTarget.value)}
          />
          <StepRulesEditor
            authority={props.authority}
            step={props.step}
            query={query()}
            onUpdateStep={props.onUpdateStep}
            rules={props.step.rules}
            onUpdateRules={props.onUpdateRules}
            advancedOpen={props.advancedOpen}
            onToggleAdvanced={props.onToggleAdvanced}
          />
        </div>
      </details>
    </div>
  );
}
